import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  activatePartnerDriver,
  assignExistingPartnerDriver,
  createPartnerDriver,
  createPartnerDriverWalletEntry,
  deactivatePartnerDriver,
  fetchPartnerDriverWallet,
  listPartnerDrivers,
  lookupPartnerDriverByPhone,
  releasePartnerDriver,
  setPartnerDriverMode,
  setPartnerDriverTripFee,
  updatePartnerDriver,
  type CreatePartnerDriverInput,
  type DriverUpdateInput,
  type PartnerDriverMode,
} from "../lib/partnerDrivers";
import {
  createReadyDriver,
  resetDriverPassword,
  type CreateReadyDriverInput,
} from "../lib/partnerAccounts";
import { usePartnerSessionStore } from "../lib/partnerAuth";

function usePartnerId() {
  return usePartnerSessionStore((s) => s.session?.partnerId ?? null);
}

export function usePartnerDriversList() {
  const partnerId = usePartnerId();
  return useQuery({
    queryKey: ["partner-drivers", partnerId],
    enabled: !!partnerId,
    queryFn: () => listPartnerDrivers(partnerId!),
    refetchInterval: 15_000,
  });
}

export function useCreatePartnerDriver() {
  const partnerId = usePartnerId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePartnerDriverInput) => {
      if (!partnerId) throw new Error("no_partner");
      return createPartnerDriver(partnerId, input);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["partner-drivers", partnerId] });
    },
  });
}

export function useDeactivatePartnerDriver() {
  const partnerId = usePartnerId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (driverId: string) => {
      if (!partnerId) throw new Error("no_partner");
      return deactivatePartnerDriver(partnerId, driverId);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["partner-drivers", partnerId] });
    },
  });
}

export function useActivatePartnerDriver() {
  const partnerId = usePartnerId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (driverId: string) => {
      if (!partnerId) throw new Error("no_partner");
      return activatePartnerDriver(partnerId, driverId);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["partner-drivers", partnerId] });
    },
  });
}

export function useUpdatePartnerDriver() {
  const partnerId = usePartnerId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { driverId: string; input: DriverUpdateInput }) => {
      if (!partnerId) throw new Error("no_partner");
      return updatePartnerDriver(partnerId, vars.driverId, vars.input);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["partner-drivers", partnerId] });
    },
  });
}

export function useSetPartnerDriverMode() {
  const partnerId = usePartnerId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ driverId, mode }: { driverId: string; mode: PartnerDriverMode }) => {
      if (!partnerId) throw new Error("no_partner");
      return setPartnerDriverMode(partnerId, driverId, mode);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ["partner-drivers", partnerId] });
      void qc.invalidateQueries({ queryKey: ["partner-driver-wallet", partnerId, vars.driverId] });
    },
  });
}

/** 170: per-trip pay override for one fleet driver. */
export function useSetPartnerDriverTripFee() {
  const partnerId = usePartnerId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ driverId, amount }: { driverId: string; amount: number | null }) => {
      if (!partnerId) throw new Error("no_partner");
      return setPartnerDriverTripFee(partnerId, driverId, amount);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["partner-drivers", partnerId] });
      void qc.invalidateQueries({ queryKey: ["partner-analytics-money"] });
      void qc.invalidateQueries({ queryKey: ["partner-analytics"] });
    },
  });
}

/** 244: look up a driver by phone before moving them into the fleet. */
export function useLookupPartnerDriverByPhone() {
  const partnerId = usePartnerId();
  return useMutation({
    mutationFn: (phone: string) => {
      if (!partnerId) throw new Error("no_partner");
      return lookupPartnerDriverByPhone(partnerId, phone);
    },
  });
}

/** 244: explicit two-step move — first call returns the client list. */
export function useAssignExistingPartnerDriver() {
  const partnerId = usePartnerId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { driverId: string; unlinkClients: boolean }) => {
      if (!partnerId) throw new Error("no_partner");
      return assignExistingPartnerDriver(partnerId, vars.driverId, vars.unlinkClients);
    },
    onSuccess: (res) => {
      if (res.ok) {
        void qc.invalidateQueries({ queryKey: ["partner-drivers", partnerId] });
      }
    },
  });
}

/** 244: partner releases a driver out of the fleet (parked, rejoinable). */
export function useReleasePartnerDriver() {
  const partnerId = usePartnerId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (driverId: string) => {
      if (!partnerId) throw new Error("no_partner");
      return releasePartnerDriver(partnerId, driverId);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["partner-drivers", partnerId] });
    },
  });
}

export function usePartnerDriverWallet(driverId: string | null) {  const partnerId = usePartnerId();
  return useQuery({
    queryKey: ["partner-driver-wallet", partnerId, driverId],
    enabled: !!partnerId && !!driverId,
    queryFn: () => fetchPartnerDriverWallet(partnerId!, driverId!),
  });
}

export function useCreatePartnerDriverWalletEntry() {
  const partnerId = usePartnerId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      driverId,
      amount,
      note,
    }: {
      driverId: string;
      amount: number;
      note?: string;
    }) => {
      if (!partnerId) throw new Error("no_partner");
      return createPartnerDriverWalletEntry(partnerId, driverId, amount, note);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: ["partner-driver-wallet", partnerId, vars.driverId],
      });
    },
  });
}

/** Ready-to-Use driver (276): server creates the Auth login + fleet driver. */
export function useCreateReadyPartnerDriver() {
  const partnerId = usePartnerId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateReadyDriverInput) => {
      if (!partnerId) throw new Error("no_partner");
      return createReadyDriver(partnerId, input);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["partner-drivers", partnerId] });
    },
  });
}

/** Partner-initiated password reset for one of the partner's fleet drivers. */
export function useResetPartnerDriverPassword() {
  const partnerId = usePartnerId();
  return useMutation({
    mutationFn: (input: { driverId: string; password?: string }) => {
      if (!partnerId) throw new Error("no_partner");
      return resetDriverPassword(partnerId, input.driverId, input.password);
    },
  });
}
